import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { MASTER_TEMPLATE_FILE_PATH, downloadFile } from '@nxsys/constants/templateFiles';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import ImportTimesheetTab from './ImportTimesheetTab';
import AgencyUploadTab from './AgencyUploadTab';
import { AGENCY_UPLOAD_FILE_TYPE } from '../constants';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';

enum IMPORT_TIMESHEET_TAB {
  IMPORTED = 'IMPORTED',
  AGENCY_UPLOAD = 'AGENCY_UPLOAD',
}

const ImportTimesheetListing = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const importFileRef = useRef(null);
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { agencyUploadSummary } = useSelector(
    (state: AppState) => state.importStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const agencyUploadCount = agencyUploadSummary?.totalTimeSheet || 0;

  const tabs = useMemo(
    () => [
      {
        tab: messages['common.imported'],
        key: IMPORT_TIMESHEET_TAB.IMPORTED,
      },
      {
        tab: agencyUploadCount
          ? `${messages['common.agencyUpload']} (${agencyUploadCount})`
          : (messages['common.agencyUpload'] as string),
        key: IMPORT_TIMESHEET_TAB.AGENCY_UPLOAD,
      },
    ],
    [agencyUploadCount],
  );

  const [activeTab, setActiveTab] = useState<IMPORT_TIMESHEET_TAB>(
    IMPORT_TIMESHEET_TAB.IMPORTED,
  );

  const handleTabChange = useCallback((key: IMPORT_TIMESHEET_TAB) => {
    setActiveTab(key);
    navigate('/import/timesheets', {
      query: {
        tab: key,
      },
    });
  }, []);

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['common.import']}
        separator="-"
        title={messages['common.timesheets']}
      />
    );
  }, [messages]);

  const renderRightContent = useCallback(() => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          name={messages['common.downloadMasterTemplate']}
          onClick={() => {
            let link = '';
            if (!isBureau) {
              link = (isIreland
          ? MASTER_TEMPLATE_FILE_PATH.umbrellaIreland.importTimesheet
          : MASTER_TEMPLATE_FILE_PATH.umbrella.importTimesheet);
            } else if (isIreland) {
              link = MASTER_TEMPLATE_FILE_PATH.ireland.importTimesheet;
            } else {
              link = MASTER_TEMPLATE_FILE_PATH.uk.importTimesheet;
            }
            downloadFile(link);
          }}
          leftIcon={
            <Icons.IconDownload
              color={defaultTheme.theme.palette.primary.main}
            />
          }
        />
        {activeTab === IMPORT_TIMESHEET_TAB.IMPORTED && (
          <AppButton
            name={messages['common.importFile']}
            onClick={() => (importFileRef?.current as any)?.open()}
            leftIcon={
              <Icons.IconFileImport
                color={defaultTheme.theme.palette.white}
              />
            }
          />
        )}
      </div>
    );
  }, [messages, activeTab]);

  useEffect(() => {
    if (tab) {
      setActiveTab(tab as IMPORT_TIMESHEET_TAB);
    }
  }, [tab]);

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case IMPORT_TIMESHEET_TAB.IMPORTED:
        return <ImportTimesheetTab importFileRef={importFileRef} />;
      case IMPORT_TIMESHEET_TAB.AGENCY_UPLOAD:
        return (
          <AgencyUploadTab fileType={AGENCY_UPLOAD_FILE_TYPE.TIMESHEETS} />
        );
      default:
        return <></>;
    }
  }, [activeTab]);

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        {!isBureau ? (
          <AppCardTabListContainer
            tabList={tabs}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
          >
            {tabContent}
          </AppCardTabListContainer>
        ) : (
          <ImportTimesheetTab importFileRef={importFileRef} />
        )}
      </AppCard>
    </AppsContainer>
  );
};

export default React.memo(ImportTimesheetListing);
